import axios from 'axios';
import io from 'socket.io-client';


// **UPDATED: Fixed getBaseURL function**
// const getBaseURL = () => {
//   // For production builds
//   if (import.meta.env.PROD) {
//     return import.meta.env.VITE_API_URL || 'https://poslah-backend.onrender.com/api/v1';
//   }
  
//   // For development - use the correct localhost port
//   if (import.meta.env.DEV) {
//     return 'http://localhost:3001/api/v1'; // ← Make sure this matches your backend port
//   }
  
//   // Fallback
//   return 'https://poslah-backend.onrender.com/api/v1';
// };

// console.log('🔗 API Client Base URL:', getBaseURL()); // This should show your correct URL

// // Create axios instance with enhanced config for Aiven
// const apiClient = axios.create({
//   baseURL: import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001',
//   timeout: 30000,
//   headers: {
//     'Content-Type': 'application/json',
//   },
// });

const getBaseURL = () => {
  // Always use environment variable first
  const envUrl = import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_API_URL;
  
  if (envUrl) {
    return envUrl;
  }
  
  // Fallback based on environment
  if (import.meta.env.PROD) {
    return 'https://api.poslah.com';
  }
  
  return 'http://localhost:3001';
};

console.log('🔗 API Client Base URL:', getBaseURL());

// Create axios instance with single base URL
const apiClient = axios.create({
  baseURL: getBaseURL(),
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});




// Retry configuration for specific routes
const retryConfig = {
  '/api/v1/auth/login': { retries: 3, timeout: 45000 }, // Increased timeout and retries
  '/api/v1/auth/register': { retries: 2, timeout: 45000 },
  default: { retries: 2, timeout: 30000 }
};

// Enhanced request interceptor
apiClient.interceptors.request.use(
  config => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Apply route-specific timeout
    const routeConfig = retryConfig[config.url] || retryConfig.default;
    config.timeout = routeConfig.timeout;
    
    console.log('Making request to:', config.method.toUpperCase(), config.url);
    console.log('Request started at:', new Date().toISOString());
    console.log('Timeout set to:', config.timeout + 'ms');
    
    return config;
  },
  error => {
    console.error('Request error:', error);
    return Promise.reject(error);
  }
);

// Enhanced response interceptor with built-in retry logic
apiClient.interceptors.response.use(
  response => {
    console.log('Response received at:', new Date().toISOString());
    return response;
  },
  async error => {
    const originalRequest = error.config;
    
    console.error('API Error:', {
      status: error.response?.status,
      message: error.response?.data?.message || error.message,
      details: error.response?.data?.details,
      url: error.config?.url,
      code: error.code
    });

    // Handle specific timeout/connection errors with retry
    if (
      (error.code === 'ECONNABORTED' || 
       error.message?.includes('timeout') || 
       error.message?.includes('Network Error')) &&
      !originalRequest._retry
    ) {
      const routeConfig = retryConfig[originalRequest.url] || retryConfig.default;
      
      if (!originalRequest._retryCount) {
        originalRequest._retryCount = 0;
      }
      
      if (originalRequest._retryCount < routeConfig.retries) {
        originalRequest._retry = true;
        originalRequest._retryCount += 1;
        
        console.log(`🔄 Retrying request (${originalRequest._retryCount}/${routeConfig.retries})...`);
        
        // Progressive delay: 1s, 2s, 4s, etc.
        const delay = Math.pow(2, originalRequest._retryCount) * 1000;
        console.log(`⏱️ Waiting ${delay}ms before retry...`);
        
        await new Promise(resolve => setTimeout(resolve, delay));
        
        // Reset timeout for retry
        originalRequest.timeout = routeConfig.timeout;
        
        return apiClient(originalRequest);
      } else {
        console.log(`❌ Max retries (${routeConfig.retries}) exceeded for ${originalRequest.url}`);
      }
    }

    // Handle 401 errors (unauthorized)
    if (error.response?.status === 401) {
      console.log('🔐 Unauthorized - redirecting to login');
      localStorage.removeItem('token');
      localStorage.removeItem('userDetails');
      
      // Only redirect if we're not already on the signin page
      if (!window.location.pathname.includes('/signin')) {
        window.location.href = '/signin';
      }
    }
    
    // Handle 504 Gateway Timeout specifically
    if (error.response?.status === 504 || error.response?.data?.code === 'CONNECTION_TIMEOUT') {
      console.log('⏱️ Database timeout detected');
    }
    
    console.log('Request failed at:', new Date().toISOString(), error.message);
    return Promise.reject(error);
  }
);

// Enhanced socket connection for production
const initializeSocket = () => {
  const token = localStorage.getItem('token');
  if (!token) {
    console.log('No token found, skipping socket connection');
    return null;
  }

  try {
    // Fix: Use proper production URL detection
    const backendUrl = import.meta.env.VITE_API_BASE_URL || 
      (window.location.hostname === 'localhost' ? 'http://localhost:3001' : 'https://api.poslah.com');
    
    console.log('🔌 Connecting socket to:', backendUrl);
    
    const socket = io(backendUrl, {
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 3000, // Increased delay
      reconnectionAttempts: 3, // Reduced attempts
      timeout: 30000, // Increased timeout for production
      // Add these for production stability
      forceNew: true,
      upgrade: true,
      rememberUpgrade: false
    });

    socket.on('connect', () => {
      console.log('🔌 Socket connected successfully');
    });

    socket.on('connect_error', (error) => {
      console.error('❌ Socket connection error:', error.message);
      
      if (error.message.includes('expired') || error.message.includes('Authentication')) {
        console.log('🔐 Socket authentication failed - disconnecting');
        socket.disconnect();
        return;
      }
    });

    socket.io.on("reconnect_attempt", (attemptNumber) => {
      console.log(`🔄 Socket reconnection attempt ${attemptNumber}`);
      const freshToken = localStorage.getItem('token');
      if (freshToken) {
        socket.auth = { token: freshToken };
      } else {
        socket.disconnect();
      }
    });

    socket.on('reconnect', (attemptNumber) => {
      console.log(`✅ Socket reconnected after ${attemptNumber} attempts`);
    });

    socket.on('disconnect', (reason) => {
      console.log('🔌 Socket disconnected:', reason);
    });

    return socket;
  } catch (error) {
    console.error('❌ Failed to initialize socket:', error);
    return null;
  }
};

// Function to send a message to the chatbot
const sendMessageToChatbot = async (message) => {
  try {
    const response = await apiClient.post('/api/v1/ai/chat', { message });
    return response.data.content;
  } catch (error) {
    console.error('Error sending message to chatbot:', error);
    return 'Sorry, I am unable to process your request at the moment.';
  }
};

// Export socket getter function
export const getSocket = () => {
  return initializeSocket();
};

// Initialize socket when module loads (optional)
window.socket = initializeSocket();

export { initializeSocket, sendMessageToChatbot };
export default apiClient;